(() => {
  if (customElements.get('product-reviews')) return;

  // Completed responses only: aborting one section cannot cancel another instance.
  const cache = new Map();
  const remember = (key, value) => {
    cache.delete(key);
    cache.set(key, value);
    if (cache.size > 32) cache.delete(cache.keys().next().value);
    return value;
  };

  class ProductReviews extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = new AbortController();
      this.meta = this.querySelector('[data-reviews-meta]');
      this.list = this.querySelector('[data-reviews-list]');
      this.button = this.querySelector('[data-reviews-more]');
      this.status = this.querySelector('[data-reviews-status]');
      this.template = this.querySelector('[data-reviews-card-template]');
      this.manualTemplate = this.querySelector('[data-reviews-manual-template]');
      this.pendingManual = Array.from(this.manualTemplate?.content.children || []);
      this.manifestPromise = null;
      this.loading = false;
      if (!this.meta || !this.list || !this.button || !this.template) return;
      this.audience = ['women', 'men'].includes(this.meta.dataset.audience) ? this.meta.dataset.audience : 'mixed';
      this.legacy = !this.meta.hasAttribute('data-audience');
      this.historyTotal = Number(this.meta.dataset.total);
      this.historyShown = this.list.querySelectorAll(':scope > :not([data-review-manual])').length;
      this.total = this.historyTotal + this.list.querySelectorAll('[data-review-manual]').length + this.pendingManual.length;
      this.shown = this.list.children.length;
      this.descending = this.dataset.order !== 'date_asc';
      this.locale = this.lang || document.documentElement.lang || 'en';
      this.button.hidden = this.shown >= this.total || !this.button.textContent.trim();
      this.button.disabled = false;
      this.status.textContent = '';
      this.setAttribute('aria-busy', 'false');
      this.button.addEventListener('click', () => this.loadMore(), { signal: this.controller.signal });
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          const index = this.pendingManual.findIndex((card) => card.dataset.reviewManual === event.detail?.blockId);
          if (index >= 0 && !this.loading) {
            const cards = this.pendingManual.splice(0, index + 1);
            this.list.append(...cards);
            this.shown += cards.length;
            this.formatManualCards(cards);
            this.button.hidden = this.shown >= this.total || !this.button.textContent.trim();
          }
          this.list.querySelector(`[data-review-manual="${CSS.escape(event.detail?.blockId || '')}"]`)?.scrollIntoView({ block: 'nearest' });
        }, { signal: this.controller.signal });
      }
      this.addEventListener('error', (event) => {
        if (event.target.matches?.('[data-review-photo]')) this.removePhoto(event.target);
      }, { capture: true, signal: this.controller.signal });
      // Already-failed server-rendered photos may have emitted error before upgrade.
      for (const img of this.querySelectorAll('[data-review-photo]')) {
        if (img.complete && img.naturalWidth === 0) this.removePhoto(img);
      }
      for (const value of this.querySelectorAll('[data-review-average]')) {
        value.textContent = new Intl.NumberFormat(this.locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(Number(value.dataset.reviewAverage));
      }
      // Match the same date formatter for initial and appended cards.
      for (const date of this.querySelectorAll('[data-review-date]')) {
        if (!date.dateTime && !date.closest('[data-review-manual]')) continue;
        this.formatDate(date, date.dateTime, !!date.closest('[data-review-manual]'));
      }
      if (this.button.hidden || this.historyShown >= this.historyTotal) return;
      const preload = () => { this.getManifest().catch(() => {}); };
      if (window.Shopify?.designMode || !('IntersectionObserver' in window)) preload();
      else {
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          preload();
        }, { rootMargin: '300px' });
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.manifestPromise = null;
    }

    async readJson(url, signal, maxBytes) {
      const key = url.href;
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      if (cache.has(key)) return cache.get(key);
      const response = await fetch(url, { signal, credentials: 'omit' });
      if (!response.ok) throw new Error('Unable to load reviews.');
      const text = await response.text();
      if (new TextEncoder().encode(text).byteLength > maxBytes) throw new Error('Review response too large.');
      // Cache only after successful parsing. Dataset validation happens at the caller.
      return remember(key, JSON.parse(text));
    }

    async getManifest() {
      if (this.manifestPromise) return this.manifestPromise;
      const controller = this.controller;
      const { productId, revision, manifest: manifestUrl } = this.meta.dataset;
      const { audience, legacy, historyTotal } = this;
      const promise = (async () => {
        const url = new URL(manifestUrl, document.baseURI);
        // A revision query also separates browser cache entries after a rebuild.
        url.searchParams.set('reviews_revision', revision);
        const manifest = await this.readJson(url, controller.signal, 1024 * 1024);
        if (manifest.schema_version !== 1 || manifest.product_id !== productId || (manifest.audience ?? (legacy ? 'mixed' : null)) !== audience || manifest.revision !== revision || manifest.total !== historyTotal || !Array.isArray(manifest.pages)) {
          cache.delete(url.href);
          throw new Error('Incompatible review dataset.');
        }
        let offset = 0;
        const prefix = `reviews-${manifest.product_id}-${legacy ? '' : `${audience}-`}${manifest.revision}-`;
        const filename = legacy ? /^reviews-\d+-[a-f0-9]{20}-\d+\.json$/ : /^reviews-\d+-(mixed|women|men)-[a-f0-9]{20}-\d+\.json$/;
        for (const page of manifest.pages) {
          if (page.offset !== offset || !Number.isInteger(page.count) || page.count < 1 || page.count > 50 || typeof page.file !== 'string' || !page.file.startsWith(prefix) || !filename.test(page.file)) {
            cache.delete(url.href);
            throw new Error('Invalid review page index.');
          }
          offset += page.count;
        }
        if (offset !== historyTotal) throw new Error('Incomplete review page index.');
        return { manifest, url, audience, legacy };
      })();
      this.manifestPromise = promise;
      try { return await promise; }
      catch (error) {
        if (this.manifestPromise === promise) this.manifestPromise = null;
        throw error;
      }
    }

    async loadMore() {
      if (this.loading || this.shown >= this.total) return;
      const controller = this.controller;
      const restoreFocus = document.activeElement === this.button;
      this.loading = true;
      this.button.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.status.classList.remove('visually-hidden');
      this.status.textContent = this.dataset.loading;
      try {
        const limit = Math.min(this.shown + (Number(this.dataset.batch) || 5), this.total);
        const manualCards = this.pendingManual.slice(0, limit - this.shown);
        const historyLimit = this.historyShown + limit - this.shown - manualCards.length;
        const reviews = [];
        const dataset = historyLimit > this.historyShown ? await this.getManifest() : null;
        // Position is the absolute offset in the canonical ascending dataset.
        for (let i = this.historyShown; i < historyLimit; i++) {
          const { manifest, url, audience, legacy } = dataset;
          const position = this.descending ? this.historyTotal - i - 1 : i;
          const entry = manifest.pages.find((page) => position >= page.offset && position < page.offset + page.count);
          if (!entry) throw new Error('Missing review page.');
          const pageUrl = new URL(entry.file, url);
          const page = await this.readJson(pageUrl, controller.signal, 64 * 1024);
          if (page.schema_version !== 1 || page.product_id !== manifest.product_id || (page.audience ?? (legacy ? 'mixed' : null)) !== audience || page.revision !== manifest.revision || page.offset !== entry.offset || !Array.isArray(page.reviews) || page.reviews.length !== entry.count) {
            cache.delete(pageUrl.href);
            throw new Error('Incompatible review page.');
          }
          reviews.push(page.reviews[position - entry.offset]);
        }
        if (controller.signal.aborted || !this.isConnected) return;
        const ids = new Set(Array.from(this.list.children, (card) => card.dataset.reviewId));
        const fragment = document.createDocumentFragment();
        for (const card of manualCards) {
          if (ids.has(card.dataset.reviewId)) throw new Error('Duplicate review.');
          ids.add(card.dataset.reviewId);
          fragment.append(card.cloneNode(true));
        }
        for (const review of reviews) {
          if (!review || typeof review.id !== 'string' || ids.has(review.id)) throw new Error('Duplicate review.');
          ids.add(review.id);
          fragment.append(this.createCard(review));
        }
        const firstNewCard = fragment.firstElementChild;
        this.formatManualCards(Array.from(fragment.children));
        this.list.append(fragment);
        for (const card of this.pendingManual.splice(0, manualCards.length)) card.remove();
        this.historyShown = historyLimit;
        this.shown = limit;
        this.status.classList.add('visually-hidden');
        this.status.textContent = this.dataset.loaded.replace('[count]', String(this.shown)).replace('[total]', String(this.total));
        if (this.shown === this.total) {
          // Preserve a useful keyboard location before removing the final button.
          if (restoreFocus && (document.activeElement === document.body || document.activeElement === this.button)) firstNewCard?.focus({ preventScroll: true });
          this.button.hidden = true;
        }
      } catch (error) {
        if (!controller.signal.aborted) this.status.textContent = this.dataset.error;
      } finally {
        if (this.controller === controller && !controller.signal.aborted) {
          this.loading = false;
          this.button.disabled = false;
          this.setAttribute('aria-busy', 'false');
          if (restoreFocus && !this.button.hidden && document.activeElement === document.body) this.button.focus({ preventScroll: true });
        }
      }
    }

    formatManualCards(cards) {
      for (const card of cards) {
        for (const date of card.querySelectorAll('[data-review-date]')) this.formatDate(date, date.dateTime, !!card.dataset.reviewManual);
      }
    }

    removePhoto(image) {
      const media = image.parentElement;
      image.remove();
      if (media?.matches('.reviews__media') && !media.children.length) media.remove();
    }

    formatDate(element, date, optional = false) {
      const parsed = new Date(`${date}T00:00:00Z`);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date) {
        if (!optional) throw new Error('Invalid review date.');
        element.hidden = true;
        return;
      }
      element.hidden = false;
      element.dateTime = date;
      element.textContent = new Intl.DateTimeFormat(this.locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`));
    }

    createCard(review) {
      if (typeof review.author !== 'string' || typeof review.body !== 'string' || !Number.isInteger(review.rating) || review.rating < 1 || review.rating > 5) throw new Error('Invalid review.');
      const card = this.template.content.firstElementChild.cloneNode(true);
      card.dataset.reviewId = review.id;
      card.querySelector('[data-review-author]').textContent = review.author;
      card.querySelector('[data-review-body]').textContent = review.body;
      const stars = card.querySelector('[data-review-rating]');
      stars.setAttribute('aria-label', this.dataset.rating.replace('[count]', String(review.rating)));
      for (const star of stars.querySelectorAll('[data-star]')) star.classList.toggle('reviews__star--empty', Number(star.dataset.star) > review.rating);
      const date = card.querySelector('[data-review-date]');
      if (date) this.formatDate(date, review.created_at);
      if (review.photo_url) {
        const url = new URL(review.photo_thumbnail_url || review.photo_url);
        if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) throw new Error('Invalid review photo.');
        const img = document.createElement('img');
        img.className = 'reviews__photo';
        img.dataset.reviewPhoto = '';
        img.alt = typeof review.photo_alt === 'string' ? review.photo_alt : '';
        img.width = Number.isInteger(review.photo_width) && review.photo_width > 0 ? review.photo_width : 400;
        img.height = Number.isInteger(review.photo_height) && review.photo_height > 0 ? review.photo_height : 400;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.src = url.href;
        const media = document.createElement('div');
        media.className = 'reviews__media';
        media.append(img);
        card.append(media);
      }
      return card;
    }
  }

  customElements.define('product-reviews', ProductReviews);
})();
